import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { LocaleContext } from "../../../routing/LangRouter";
import { ItineraryContext } from "../../../constants/layouts/itinerary-layout";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { saveHotelSelectedFilters } from "../../../store/actions";

//import utils
import { calculateDateDifferenceInDays, formatGregorianDate, formatPersianDate } from "../../../utils/date";
import { numberWithCommas } from "../../../utils/utility";

//import general components
import { BaseButton } from "../../common/base-button/base-button";

//import images and icons
import noImage from "../../../assets/imgs/no-image.png";

// import styles
import "./int-hotel-room-item.scss";

function IntHotelRoomsItem({ dispatch, roomList }) {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const { routes, setRoutes } = useContext(ItineraryContext);

    //sessionStorage variables
    const { checkIn, checkOut } = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation"));
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));

    // Calculate the length of stay (in days) between check-in and check-out dates
    const dateLength = useMemo(() => {
        return calculateDateDifferenceInDays(checkIn, checkOut);
    }, [checkIn, checkOut]);

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveHotelSelectedFilters(null));
    };

    //select room
    const saveSelectedRooms = room => {
        resetFilters();
        sessionStorage.setItem("selectedIntRooms", JSON.stringify(room));
        if (generalInformation?.isItinerary) {
            updateItineraryGenerate();
            updateRoutes();
            navigate(`/${locale}/itinerary/itinerary-page`);
        } else {
            navigate(`/${locale}/int-hotel-passengers`);
        }
    };
    // update the layout routes
    const updateRoutes = () => {
        const newRoute = `/${locale}/itinerary/itinerary-page`;
        setRoutes([...routes, newRoute]);
        sessionStorage.setItem("visitedRoutes", JSON.stringify([...routes, newRoute]));
    };

    //update the itinerary generate to true
    const updateItineraryGenerate = () => {
        itineraryInformation.itineraryGenerate = true;
        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
    };

    //room counter
    const counterList = {
        0: t("Hotel.First Room"),
        1: t("Hotel.Second Room"),
        2: t("Hotel.Third Room"),
        3: t("Hotel.Fourth Room"),
        4: t("Hotel.Fifth Room"),
        5: t("Hotel.Sixth Room"),
        6: t("Hotel.Seventh Room"),
        7: t("Hotel.Eighth Room"),
        8: t("Hotel.Ninth Room"),
        9: t("Hotel.Tenth Room"),
    };

    return (
        <Grid container item xs={12} rowGap={3} justifyContent="center" alignItems="flex-start">
            {roomList?.map(roomItem => {
                return (
                    <Grid
                        key={roomItem?.id}
                        className="int-hotel-room-item__room-suggest"
                        container
                        item
                        xs={12}
                        rowGap={1}
                        justifyContent="space-between"
                    >
                        <Grid container item md={9.5} rowGap={{ xs: 1, md: 3 }}>
                            {Object.values(roomItem?.rooms ?? {}).map((room, index) => {
                                // Fetch cancellation policies for the room (if available)
                                const cancellationPolicies = roomItem?.cancellation_policies?.[index + 1] ?? [];
                                return (
                                    <Grid
                                        className="int-hotel-room-item__room-item"
                                        container
                                        item
                                        xs={12}
                                        columnGap={{ xs: 1, md: 2 }}
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Grid
                                            className="int-hotel-room-item__room-index"
                                            container
                                            item
                                            xs="auto"
                                            justifyContent="center"
                                            alignItems="center"
                                        >
                                            {counterList?.[index]}
                                        </Grid>
                                        <Grid
                                            className="int-hotel-room-item__room-image"
                                            component="img"
                                            src={noImage}
                                            display={{ xs: "none", md: "flex" }}
                                        />
                                        <Grid container item xs={9} md={8} rowGap={1}>
                                            <Grid
                                                container
                                                item
                                                xs={12}
                                                columnGap={{ xs: 1, md: 2 }}
                                                justifyContent={{ xs: "space-between", md: "flex-start" }}
                                                alignItems={{ xs: "flex-start", md: "center" }}
                                            >
                                                <span className="int-hotel-room-item__room-name">{room?.name ?? t("unspecified")}</span>
                                                <span className="int-hotel-room-item__room-capacity">
                                                    {(room?.travelers?.adult_count?.[index + 1] ?? 0) +
                                                        (room?.travelers?.child_count?.[index + 1] ?? 0)}{" "}
                                                    {t("person")}
                                                </span>
                                            </Grid>
                                            <Grid container item xs={12} columnGap={1} alignItems="center">
                                                <span className="int-hotel-room-item__details active">{room?.meal_type ?? t("unspecified")}</span>
                                                <span className={`int-hotel-room-item__details ${room?.non_refundable ? "" : "active"}`}>
                                                    {" "}
                                                    {t("Hotel.Cancellation Available")}
                                                </span>
                                            </Grid>
                                            {/* Display cancellation policies */}
                                            {cancellationPolicies?.length > 0 && (
                                                <Grid container item xs={12} direction="column" rowGap={1}>
                                                    {(() => {
                                                        const policy = cancellationPolicies[0]; // Get only the first policy
                                                        // Convert the date to Persian calendar
                                                        const date = policy?.from_date?.split("T")[0];

                                                        return (
                                                            <Box sx={{ marginY: 1 }}>
                                                                <Typography variant="body1" sx={{ fontSize: { xs: "0.75rem", md: "1rem" } }}>
                                                                    {t("General.Free Cancellation Until")}{" "}
                                                                    <Typography
                                                                        component="span"
                                                                        color="primary"
                                                                        sx={{ fontSize: { xs: "0.75rem", md: "1rem" } }}
                                                                    >
                                                                        {locale === "fa"
                                                                            ? formatPersianDate(date, {
                                                                                  day: "numeric",
                                                                                  month: "long",
                                                                                  year: undefined,
                                                                                  weekday: undefined,
                                                                              })
                                                                            : formatGregorianDate(date, null, "YYYY/MM/DD")}
                                                                    </Typography>
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })()}
                                                </Grid>
                                            )}

                                            {roomItem?.remarks && roomItem.remarks.length > 0 && (
                                                <Grid container item xs={12}>
                                                    <span className="int-hotel-room-item__details active">{roomItem.remarks[0]}</span>
                                                </Grid>
                                            )}
                                            {roomItem?.extra_charge?.excluded && (
                                                <Grid container item xs={12}>
                                                    <span className="int-hotel-room-item__details active">
                                                        Excluded: {roomItem.extra_charge.excluded}
                                                    </span>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        <Divider flexItem orientation="vertical" sx={{ display: { xs: "none", md: "flex" } }} />

                        <Grid
                            container
                            item
                            xs={12}
                            md={2}
                            columnGap={1}
                            direction={{ xs: "row", md: "column" }}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Grid container item xs="auto" md={1} rowGap={1}>
                                <Grid
                                    className="int-hotel-room-item__price-title"
                                    component="span"
                                    container
                                    item
                                    xs={12}
                                    justifyContent={{ xs: "flex-start", md: "center" }}
                                >
                                    {t("Price.Price Per Number of Nights", { dateLength })}:
                                </Grid>
                                <Grid className="int-hotel-room-item__price" component="span" container item xs={12} justifyContent="center">
                                    {numberWithCommas(roomItem?.fare?.total ?? 0)} {t("currency")}
                                </Grid>
                            </Grid>

                            <Grid container item xs={4} md={1}>
                                <BaseButton
                                    text={t("booking")}
                                    color="primary"
                                    radius="radius-10"
                                    size="xlarge"
                                    handleClick={() => saveSelectedRooms(roomItem)}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                );
            })}
        </Grid>
    );
}

export default connect()(IntHotelRoomsItem);

//revalidate rooms

//  const { revalidateBooking } = useRevalidate(generalInformation, setLoading);
//     const checkRate = async room => {
//         let validationValue;
//         await revalidateBooking(room?.fare_source_code, "hotel").then(newPrice => {
//             validationValue = newPrice === room?.fare?.total ? true : false;
//         });

//         if (validationValue) {
//             saveSelectedRooms(room);
//         } else {
//             toast.error(t("Toast Messages.Invalid Room Selection"), { toastId: "custom-id-yes" });
//             setLoading(false);
//         }
//     };
